// src/pages/Users/Galleries/components/AlbumPreviewModal.jsx

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaImage,
  FaTimes,
} from "react-icons/fa";

import {
  FALLBACK_ALBUM_IMG,
  getPreviewPhotos,
  stripHtml,
} from "../utils/galleriesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_ALBUM_IMG;
  }
}

export default function AlbumPreviewModal({ isOpen, album, onClose }) {
  const navigate = useNavigate();

  const photos = useMemo(() => getPreviewPhotos(album, 12), [album]);
  const totalPhotos = Array.isArray(album?.photos) ? album.photos.length : 0;

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !album) return null;

  const openAlbum = () => {
    onClose?.();
    navigate(`/galerie-photos/album/${album.id}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/75 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Prévisualisation ${album?.titre || "album"}`}
      onClick={onClose}
    >
      <article
        className="mx-auto flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="border-b border-slate-200 p-5 dark:border-slate-800 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-600 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-300">
                <FaImage />
                Prévisualisation
              </span>

              <h2 className="mt-4 line-clamp-2 text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
                {album?.titre || "Album"}
              </h2>

              <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                {totalPhotos} photo{totalPhotos > 1 ? "s" : ""} —{" "}
                {photos.length} affichée{photos.length > 1 ? "s" : ""} en aperçu
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-lg ring-1 ring-slate-200 transition hover:text-slate-950 active:scale-95 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:text-white"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="overflow-y-auto p-5 sm:p-6">
          {album?.description && (
            <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-sm leading-7 text-slate-700 dark:text-slate-300">
                {stripHtml(album.description)}
              </p>
            </div>
          )}

          {photos.length === 0 ? (
            <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
              <FaImage className="mx-auto text-4xl text-slate-300" />
              <p className="mt-4 text-sm font-semibold text-slate-500 dark:text-slate-400">
                Aucune photo à prévisualiser.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((photo, index) => (
                <button
                  key={photo.id || `${album.id}-${index}`}
                  type="button"
                  onClick={openAlbum}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-950"
                  aria-label="Voir toutes les photos"
                >
                  <div className="aspect-[16/10] w-full">
                    <img
                      src={photo.absolute_url || FALLBACK_ALBUM_IMG}
                      alt={stripHtml(photo.description || `Photo ${index + 1}`)}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                      loading="lazy"
                      onError={onImgError}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Pour éviter les problèmes d’affichage sur mobile, la
              prévisualisation est volontairement limitée.
            </p>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 shadow-sm transition active:scale-95 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                Fermer
              </button>

              <button
                type="button"
                onClick={openAlbum}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 text-sm font-bold text-white shadow-lg shadow-orange-600/20 transition hover:bg-orange-700 active:scale-95"
              >
                Voir toutes les photos
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
}